<template>
  <div>
    <div class="filter-container">
      <label for="status-filter" class="filter-label">Filtrar por:</label>
      <select id="status-filter" v-model="selectedStatusFilter" class="status-filter-select">
        <option :value="null">Todos los estatus</option>
        <option v-for="status in statusFilterOptions" :key="status.value" :value="status.value">
          {{ status.label }}
        </option>
      </select>
    </div>
    <div class="table-scroll">
      <table class="data-table">
        <thead class="table-header">
          <tr>
            <th>
              <span class="sortable-header" @click="toggleNameSort">
                EMPRESA
                <img
                  v-if="nameSortDirection === 'asc'"
                  src="@/assets/asc-order-arrow.svg"
                  alt="Asc"
                  width="12"
                  height="12"
                />
                <img v-else src="@/assets/desc-order-arrow.svg" alt="Desc" width="12" height="12" />
              </span>
            </th>
            <th>ESTATUS DE LA EMPRESA</th>
            <th>
              <span class="sortable-header" @click="toggleDateSort">
                FECHA DE CREACIÓN
                <img
                  v-if="dateSortDirection === 'asc'"
                  src="@/assets/asc-order-arrow.svg"
                  alt="Asc"
                  width="12"
                  height="12"
                />
                <img v-else src="@/assets/desc-order-arrow.svg" alt="Desc" width="12" height="12" />
              </span>
            </th>
            <th>REGISTROS COMPLETOS</th>
            <th>AGENTE</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in filteredTableData"
            :key="item.id"
            @click="onRowClick(item)"
            :class="{ 'row-alt': index % 2 === 1 }"
            class="table-row"
          >
            <td>{{ item.name }}</td>
            <td class="status-cell">
              <CompanyStatusSelect
                :model-value="item.status"
                @update:model-value="(value) => onStatusChange(item, value)"
                @click.stop
              />
            </td>
            <td>{{ formatDate(item.createdAt) }}</td>
            <td
              class="records-cell"
              :class="{
                'records-complete':
                  (item.employeesCount ?? 0) - (item.errorEmployeesCount ?? 0) ===
                  (item.employeesCount ?? 0),
                'records-incomplete':
                  (item.employeesCount ?? 0) - (item.errorEmployeesCount ?? 0) <
                  (item.employeesCount ?? 0),
              }"
            >
              <span class="records-fraction">
                {{
                  (item.employeesCount ?? 0) === 0
                    ? '-'
                    : `${(item.employeesCount ?? 0) - (item.errorEmployeesCount ?? 0)}/${item.employeesCount ?? 0}`
                }}
              </span>
            </td>
            <td>
              {{ agent(item.agentKey || defaultAgentKey) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Toast Message -->
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :duration="3000"
      :type="toastType"
      :icon="toastIcon"
    />
    <CustomLoading :is-visible="isLoadingCompanies" text="Cargando grupos..." />
  </div>
</template>

<script setup lang="ts">
import { useBulkLoadStore } from '../stores/bulkLoad'
import { ApiService } from '../services/apiService'
import { inject, onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import type { GroupResponseDTO, CompanyDto, AgentDTO } from 'commons'
import { CompanyStatus } from '../../../commons/src/groups-management/utils/company-status'
import { WebEnvironmentConfigService } from '../services/env-config.service'
import ToastMessage from './ToastMessage.vue'
import CustomLoading from './CustomLoading.vue'
import CompanyStatusSelect from './CompanyStatusSelect.vue'
import { trackGroupEvent } from '@/utils/googleAnalyticsEventsSender'
import errorIcon from '@/assets/error-icon-white.svg'
import checkIcon from '@/assets/check-icon.svg'

const tableData = ref<CompanyDto[]>([])
const apiService = inject<ApiService>('apiService')
const bulkLoadStore = useBulkLoadStore()
const router = useRouter()

const envConfig = new WebEnvironmentConfigService()
const showToast = ref(false)
const defaultAgentKey = ref<string>('')
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)
const isLoadingCompanies = ref(false)
const selectedStatusFilter = ref<CompanyStatus | null>(null)
const nameSortDirection = ref<'asc' | 'desc'>('desc')
const dateSortDirection = ref<'asc' | 'desc'>('desc')
const activeSort = ref<'name' | 'date'>('date') // Por defecto ordenar por fecha

const statusFilterOptions = Object.values(CompanyStatus).map((status) => ({
  value: status,
  label: status,
}))

function toggleNameSort() {
  activeSort.value = 'name'
  nameSortDirection.value = nameSortDirection.value === 'asc' ? 'desc' : 'asc'
}

function toggleDateSort() {
  activeSort.value = 'date'
  dateSortDirection.value = dateSortDirection.value === 'desc' ? 'asc' : 'desc'
}

const filteredTableData = computed(() => {
  let data = tableData.value.slice()

  // Aplicar filtro por estatus
  if (selectedStatusFilter.value) {
    data = data.filter((item) => item.status === selectedStatusFilter.value)
  }

  // Aplicar ordenamiento según la columna activa
  if (activeSort.value === 'name') {
    data = data.slice().sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      if (nameA < nameB) return nameSortDirection.value === 'asc' ? -1 : 1
      if (nameA > nameB) return nameSortDirection.value === 'asc' ? 1 : -1
      return 0
    })
  } else {
    // Ordenamiento por fecha
    data = data.slice().sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
      if (dateA < dateB) return dateSortDirection.value === 'desc' ? 1 : -1
      if (dateA > dateB) return dateSortDirection.value === 'desc' ? -1 : 1
      return 0
    })
  }

  return data
})

const props = defineProps<{
  availableAgents: AgentDTO[]
}>()
function agent(agentKey: string) {
  const found = props.availableAgents.find((a) => a.userName === agentKey)
  return found ? `${found.nombre} ${found.apPaterno} ${found.apMaterno}` : 'Sin asignar'
}

function truncateCompanyName(name: string, maxLength: number = 20): string {
  if (name.length <= maxLength) {
    return name
  }
  return name.substring(0, maxLength) + '...'
}

function formatDate(date: Date | string | undefined): string {
  if (!date) return ''
  const parsedDate = typeof date === 'string' ? new Date(date) : date
  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) return ''
  return parsedDate.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
onMounted(async () => {
  defaultAgentKey.value = await envConfig.getDefaultGeneralAgentKey()
  fetchCompanies()
})

if (!apiService) {
  throw new Error('ApiService is not injected')
}

const fetchCompanies = async () => {
  try {
    isLoadingCompanies.value = true
    const response = await apiService.getCompanies()
    tableData.value = response
  } catch (error) {
    console.error('Hubo un error: Error fetching companies ', error)
    tableData.value = []
  } finally {
    isLoadingCompanies.value = false
  }
}

const onRowClick = async (item: CompanyDto) => {
  trackGroupEvent('acceso_empresa_' + item.name, { location: 'Listado_grupos' })
  const group = {} as GroupResponseDTO
  group.nombrePublico = item.name
  group.descripcion = item.description
  group.adminEmail = item.adminEmail
  group.id = item.businessId
  group.idEmpresa = item.idEmpresa || ''

  bulkLoadStore.setSelectedGroup(group)
  bulkLoadStore.setPublicGroupName(item.name || '')
  bulkLoadStore.setSelectedAgent(item.agentKey || defaultAgentKey.value)
  bulkLoadStore.setAvailableAgents(props.availableAgents)
  router.push({ name: 'edit-bulk-load-data' })
}

const onStatusChange = async (item: CompanyDto, newStatus: CompanyStatus) => {
  // Si el status no cambió, no hacer nada
  if (item.status === newStatus) {
    return
  }

  const previousStatus = item.status
  // Actualizar localmente primero para feedback inmediato
  item.status = newStatus

  try {
    await apiService.updateCompanyStatus(item.businessId, newStatus)
    // Mostrar toast de éxito
    toastMessage.value = truncateCompanyName(item.name)
    toastSubmessage.value = `Estatus actualizado a ${newStatus}`
    toastType.value = 'success'
    toastIcon.value = checkIcon
    showToast.value = true
  } catch (error) {
    // Revertir el cambio local en caso de error
    item.status = previousStatus
    console.error('Error al actualizar estatus:', error)
    // Mostrar toast de error
    toastMessage.value = truncateCompanyName(item.name)
    toastSubmessage.value = 'No se pudo actualizar el estatus'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}
</script>

<style scoped>
.data-table {
  border: none;
  border-collapse: collapse;
  position: relative;
}
.data-table thead tr {
  background-color: #003896;
  color: #fff;
}
.data-table tr {
  background-color: #fff;
  border: none;
  position: relative;
}
.data-table tr.row-alt {
  background-color: #f4f7fa;
}
.data-table th,
.data-table td {
  border: none;
}
.data-table tbody tr:hover {
  background-color: #e0e7ef;
  cursor: pointer;
}

.table-row {
  position: relative;
}

.url-column {
  text-align: center;
  width: 70px;
  min-width: 70px;
  max-width: 70px;
}

.copy-url-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-url-btn:hover {
  background-color: #f0f0f0;
}

.copy-url-btn.copied {
  background-color: #e8f5e8;
}

.copy-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.2s ease;
}

.copy-url-btn:hover .copy-icon {
  transform: scale(1.1);
}

.copy-url-btn.copied .copy-icon {
  filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg)
    brightness(104%) contrast(97%);
}

.status-cell {
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.filter-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-label {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #333333;
}

.status-filter-select {
  box-sizing: border-box;
  padding: 8px 32px 8px 12px;
  min-width: 200px;
  height: 40px;
  border: 1px solid #c5c5c5;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #333333;
  background-color: #ffffff;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M2 2l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px 8px;
}

.status-filter-select:hover {
  border-color: #a1a1a1;
}

.status-filter-select:focus {
  border-color: #003286;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: inherit;
  font-weight: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.records-cell {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.records-fraction {
  letter-spacing: 2px;
  white-space: nowrap;
  font-weight: 500 !important;
}

.records-complete {
  color: #4eab1f;
}

.records-incomplete {
  color: #d1721f;
}
</style>
